import http from './httpClient';

class PermissionService {
  // Cache for user permissions
  static userPermissionsCache = null;

  // Get current user's permissions
  static async getUserPermissions() {
    if (this.userPermissionsCache) {
      return this.userPermissionsCache;
    }

    try {
      const data = await http.get('/auth/my-permissions');
      if (data.success) {
        this.userPermissionsCache = data.permissions || [];
        return this.userPermissionsCache;
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch user permissions:', error);
      return [];
    }
  }

  // Clear permission cache (call on logout)
  static clearCache() {
    this.userPermissionsCache = null;
  }

  // Check if user has a specific permission
  static async hasPermission(permissionName) {
    const permissions = await this.getUserPermissions();
    return permissions.some(p => p.permission_name === permissionName);
  }

  // Check if user has any of the given permissions
  static async hasAnyPermission(permissionNames) {
    const permissions = await this.getUserPermissions();
    return permissionNames.some(name => 
      permissions.some(p => p.permission_name === name)
    );
  }

  // Get navigation items based on user permissions
  static async getFilteredNavigation() {
    const permissions = await this.getUserPermissions();
    const permissionNames = permissions.map(p => p.permission_name);

    const navigationConfig = [
      {
        id: 'overview',
        label: 'Overview',
        screen: 'dashboard',
        requiredPermissions: [], // Everyone can see dashboard
        icon: '📊'
      },
      {
        id: 'inventory',
        label: 'Inventory',
        screen: 'inventory',
        requiredPermissions: ['view_inventory'],
        icon: ''
      },
      {
        id: 'stock-in',
        label: 'Stock In',
        screen: 'stock-in',
        requiredPermissions: ['stock_in'],
        icon: '📥'
      },
      {
        id: 'stock-out',
        label: 'Stock Out',
        screen: 'stock-out',
        requiredPermissions: ['stock_out'],
        icon: '📤'
      },
      {
        id: 'usage',
        label: 'Usage',
        screen: 'usage',
        requiredPermissions: ['view_inventory'],
        icon: '📊'
      },
      {
        id: 'menu',
        label: 'Menu',
        screen: 'itemmap',
        requiredPermissions: ['view_menu'],
        icon: '🍽️'
      },
      {
        id: 'recipes',
        label: 'Recipes',
        screen: 'itemmap',
        requiredPermissions: ['manage_recipes'],
        icon: '📖'
      },
      {
        id: 'sales',
        label: 'Sales Report',
        screen: 'sales-report',
        requiredPermissions: ['view_sales'],
        icon: '💰'
      },
      {
        id: 'todays-sales',
        label: 'Today\'s Sales',
        screen: 'todays-sales-report',
        requiredPermissions: ['view_sales'],
        icon: '📈'
      },
      {
        id: 'reports',
        label: 'Reports',
        screen: 'report',
        requiredPermissions: ['view_reports'],
        icon: '📄'
      },
      {
        id: 'vendors',
        label: 'Vendors',
        screen: 'vendors',
        requiredPermissions: ['view_vendors'],
        icon: '🚚'
      },
      {
        id: 'xyz',
        label: 'XYZ Analysis',
        screen: 'xyz',
        requiredPermissions: ['view_reports'],
        icon: '📊'
      },
      {
        id: 'qr-owner-dashboard',
        label: 'Owner Dashboard',
        screen: 'qr-owner-dashboard',
        requiredPermissions: ['view_orders', 'view_sales'],
        icon: '👔'
      },
      {
        id: 'chef-dashboard',
        label: 'Chef Dashboard',
        screen: 'chef-dashboard',
        requiredPermissions: ['view_orders', 'manage_orders'],
        icon: '👨‍🍳'
      },
      {
        id: 'waiter-dashboard',
        label: 'Waiter Dashboard',
        screen: 'waiter-dashboard',
        requiredPermissions: ['view_orders'],
        icon: '🍽️'
      },
      {
        id: 'settings',
        label: 'Settings',
        screen: 'settings',
        requiredPermissions: ['view_settings'],
        icon: '⚙️'
      }
    ];

    // Filter navigation based on permissions
    return navigationConfig.filter(item => {
      if (item.requiredPermissions.length === 0) return true;
      return item.requiredPermissions.some(perm => permissionNames.includes(perm));
    });
  }
}

export default PermissionService;
